import { useCallback, useEffect, useState } from 'react';
import type { CaptionMode } from '../../../types/messaging';

const STORAGE_KEY = 'loopy.voiceChat.captionMode';

const ORDER: CaptionMode[] = ['always', 'while_playing', 'never'];

/**
 * useCaptionMode — local-storage-backed caption preference for AI Chat
 * Buddy voice mode. Three states cycle in this order:
 *   always → while_playing → never → always
 *
 * The user message bubble always shows the transcript (so learners can
 * verify what Whisper heard). This setting only controls the bot reply
 * bubble's caption visibility.
 *
 * NB: per spec we will eventually round-trip this through
 * `users.preferences` so it syncs across devices. For Phase 1 we keep it
 * local-only — the server route doesn't exist yet and shipping a
 * preference that can drift across devices is fine for an MVP.
 */
export function useCaptionMode() {
    const [mode, setMode] = useState<CaptionMode>(() => readStored());

    useEffect(() => {
        try {
            window.localStorage.setItem(STORAGE_KEY, mode);
        } catch { /* private mode — ignore */ }
    }, [mode]);

    const cycle = useCallback(() => {
        setMode((current) => {
            const i = ORDER.indexOf(current);
            return ORDER[(i + 1) % ORDER.length];
        });
    }, []);

    return { mode, setMode, cycle };
}

function readStored(): CaptionMode {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw === 'always' || raw === 'while_playing' || raw === 'never') {
            return raw;
        }
    } catch { /* noop */ }
    return 'always';
}
